import { ApiConfig } from "../../constants";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ApiService from "services/api.service";
import { getResources } from "pages/Segment/SegmentHelpers";
import SlackTemplateHeader from "pages/SlackBuilder/SlackTemplateHeader";
import MergeTagInput from "components/MergeTagInput";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import Progress from "components/Progress";

function safeJsonParse(jsonString: string) {
  try {
    // Attempt to parse the JSON
    return JSON.parse(jsonString);
  } catch (error) {
    // If parsing fails, return null, undefined, or a custom error message
    console.error("Invalid JSON:", error);
    return {
      fileType: 1,
      fileName: "",
      documentUrl: "",
      whatsappText: jsonString,
    };
  }
}

const WhatsappBuilder = () => {
  const { id } = useParams();
  const [whatsappMessage, setWhatsappMessage] = useState<string>("");
  const [templateName, setTemplateName] =
    useState<string>(`My Whataspp template`);
  const [whatsappTemplateId, setWhatsappTemplateId] = useState<string>("");
  const [possibleAttributes, setPossibleAttributes] = useState<string[]>([]);
  const [isPreview, setIsPreview] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [fileType, setFileType] = useState("3");
  const [documentUrl, setDocumentUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [vFirstTemplateId, setvFirstTemplateId] = useState("");

  const getTemplate = async (templateId: string) => {
    return ApiService.get({
      url: `${ApiConfig.getAllTemplates}/${templateId}`,
    });
  };

  const onSave = async () => {
    setIsSaving(true);

    const content = JSON.stringify({
      fileType,
      fileName,
      documentUrl,
      vFirstTemplateId,
      whatsappMessage,
    });

    try {
      const reqBody = {
        name: templateName,
        whatsappText: content,
        type: "whatsapp",
      };

      await ApiService.patch({
        url: `${ApiConfig.getAllTemplates}/${id}`,
        options: {
          ...reqBody,
        },
      });
    } catch (e) {
      let message = "Unexpected error";
      if (e instanceof AxiosError) {
        message = e.response?.data?.message?.[0] || e.response?.data?.message;
      }
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  useLayoutEffect(() => {
    const populateWhatsappBuilder = async () => {
      try {
        const { data } = await getTemplate(id);
        const parseData = safeJsonParse(data.whatsappText);
        setWhatsappMessage(parseData.whatsappMessage || "");
        setDocumentUrl(parseData.documentUrl);
        setFileName(parseData.fileName);
        setFileType(parseData.fileType);
        setvFirstTemplateId(parseData.vFirstTemplateId);
        setTemplateName(data.name);
        setWhatsappTemplateId(data.id);
      } catch (e) {
        toast.error("Error while loading");
      } finally {
        setIsLoading(false);
      }
    };
    const loadAttributes = async () => {
      const { data } = await getResources("attributes");
      setPossibleAttributes(
        data.options.map((option: { label: string }) => option.label)
      );
    };
    populateWhatsappBuilder();
    loadAttributes();
  }, []);

  const onPersonalizeClick = () => {
    const focusedInput = document.querySelector(
      "#whatsappMessage"
    ) as HTMLInputElement;
    const indexToInsert =
      focusedInput?.selectionStart || whatsappMessage.length;
    const newWhatsappMessageArr = whatsappMessage.split("");
    newWhatsappMessageArr.splice(indexToInsert, 0, "{{}}");
    setWhatsappMessage(newWhatsappMessageArr.join(""));
    setIsPreview(true);
  };

  if (isLoading) return <Progress />;

  return (
    <div className="w-full relative w-full">
      <SlackTemplateHeader
        onPersonalizeClick={onPersonalizeClick}
        onSave={onSave}
        loading={isSaving}
        templateName={templateName}
        handleTemplateNameChange={(e) => setTemplateName(e.target.value)}
      />
      <div style={{ width: "490px", margin: "auto" }}>
        <label
          htmlFor="file-type"
          className="block text-sm font-medium text-gray-700"
        >
          Choose a type:
        </label>
        <select
          id="file-type"
          name="file-type"
          value={fileType}
          onChange={(e) => setFileType(e.target.value)}
        >
          <option value="3">Document</option>
          <option value="1">Text</option>
        </select>
        {/* Document URL input */}
        <div>
          <label
            htmlFor="template-id"
            className="block text-sm font-medium text-gray-700"
          >
            Template ID:
          </label>
          <input
            type="text"
            id="template-id"
            name="templateId"
            value={vFirstTemplateId}
            onChange={(e) => setvFirstTemplateId(e.target.value)}
            placeholder="Enter document URL"
          />
        </div>
        <div>
          <label
            htmlFor="document-url"
            className="block text-sm font-medium text-gray-700"
          >
            Document URL:
          </label>
          <input
            type="url"
            id="document-url"
            name="documentUrl"
            value={documentUrl}
            onChange={(e) => setDocumentUrl(e.target.value)}
            placeholder="Enter document URL"
          />
        </div>

        {/* File name input */}
        <div>
          <label
            htmlFor="file-name"
            className="block text-sm font-medium text-gray-700"
          >
            File Name:
          </label>
          <input
            type="text"
            id="file-name"
            name="fileName"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="Enter file name"
          />
        </div>

        {/* Text area for text */}
        <div>
          <label
            htmlFor="text-content"
            className="block text-sm font-medium text-gray-700"
          >
            Text Content:
          </label>
          <textarea
            id="whatsappMessage"
            name="textContent"
            value={whatsappMessage}
            onChange={(e) => setWhatsappMessage(e.target.value)}
            placeholder="Enter text here"
          />
          {/* <textarea onChange={(e) => setWhatsappMessage(e.target.value)}>
          {whatsappMessage}
        </textarea> */}
        </div>
        {/* <MergeTagInput
          isRequired
          value={whatsappMessage}
          placeholder={"Whatsapp Message"}
          name="whatsappMessage"
          id="whatsappMessage"
          fullWidth
          setValue={setWhatsappMessage}
          onChange={(e) => setWhatsappMessage(e.target.value)}
          labelShrink
          isPreview={isPreview}
          setIsPreview={setIsPreview}
          possibleAttributes={possibleAttributes}
          inputRef={inputRef}
        /> */}
        <div className="text-gray-500">
          <h3>Things you should notice:</h3>
          <div className="text-[12px]">
            <div>
              * if message length is over 160 it will be splitted into multiple
              parts
            </div>
            <div>
              * if message length is over 1600 it will be limited to first 1600
              characters to meet system's requirements
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsappBuilder;
